import { useTranslation } from 'react-i18next';
import { useBulkUpload } from '@/api/recognition';
import { bulkRequestErrorMessage } from './errorMessages';
import { toRequestRows } from './rowUtils';
import type { BulkEditableRow } from './types';

interface Props {
  rows: BulkEditableRow[];
  campaignId: string | null;
  /** Programa del wizard: el backend valida el lote con SUS reglas, no con las de Fundamentales. */
  programa?: string | null;
  onBack: () => void;
  onDone: () => void;
}

/** Paso 4 del wizard: confirma el lote completo y muestra el resultado real. */
export function StepConfirm({ rows, campaignId, programa, onBack, onDone }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const commit = useBulkUpload();
  const validRows = rows.filter((row) => row.status === 'valid');

  /**
   * Se envía el lote completo, no solo las filas válidas: el backend revalida cada fila y calcula
   * `skipped` sobre lo que recibe, así que omitir filas aquí haría que el resumen final reportara
   * 0 omitidas y las filas con error desaparecieran del reporte.
   */
  const confirm = () => {
    commit.mutate({ rows: toRequestRows(rows), campanaId: campaignId, preview: false, programa });
  };

  if (commit.isSuccess) {
    const result = commit.data;
    return (
      <div className="bulk-success" role="status">
        <div className="bulk-success-icon">✅</div>
        <p>{t('bulk.committed', { created: result.created, skipped: result.skipped })}</p>
        {(result.duplicadosOmitidos ?? 0) > 0 && (
          <p className="bulk-duplicates-note">
            <i className="fas fa-circle-info" /> {t('bulk.duplicatesSkipped', { count: result.duplicadosOmitidos })}
          </p>
        )}
        {result.receptoresNoEncontrados.length > 0 && (
          <div className="bulk-not-found">
            <p>{t('bulk.notFoundTitle')}</p>
            <ul>{result.receptoresNoEncontrados.map((email) => <li key={email}>{email}</li>)}</ul>
          </div>
        )}
        <button type="button" className="btn btn--send" onClick={onDone}>{t('common.close')}</button>
      </div>
    );
  }

  return (
    <div className="bulk-step bulk-step--confirm">
      <p>{t('bulk.confirmSummary', { count: validRows.length })}</p>
      {commit.isError && (
        <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {bulkRequestErrorMessage(t, commit.error)}</p>
      )}
      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onBack} disabled={commit.isPending}>
          <i className="fas fa-arrow-left" /> {t('bulk.back')}
        </button>
        <button type="button" className="btn btn--send" disabled={validRows.length === 0 || commit.isPending} onClick={confirm}>
          <i className="fas fa-cloud-arrow-up" /> {t('bulk.confirm', { count: validRows.length })}
        </button>
      </div>
    </div>
  );
}

export default StepConfirm;
